// Wire protocol between the mobile app and the desktop, carried by the
// Cloudflare relay worker (cloudflare/relay). The worker forwards frames
// between the two roles verbatim; it only interprets `auth` and `ping`.
//
// Mobile runs the desktop renderer with `window.api` swapped for a
// relay-backed shim, so the protocol is deliberately thin: a request names an
// IPC channel and carries its positional arguments, and an event carries the
// argument list a `webContents.send` would have delivered.

/**
 * Whether a relay host is on the loopback interface, and so has no
 * certificate. Everything else must be TLS — the channel carries terminal
 * output, file contents and git diffs in the clear otherwise. Both roles use
 * this so they can never disagree about a host's scheme and fail to connect.
 */
export function isLocalRelayHost(host: string): boolean {
  return /^(localhost|127\.0\.0\.1|\[::1\])(:|$)/.test(host)
}

/** A control frame both roles send to the worker on connect. */
export interface AuthMessage {
  type: 'auth'
  channelId: string
  /** Epoch millis; the worker rejects anything outside a 5 minute window. */
  timestamp: number
  /** HMAC-SHA256 of `${channelId}:${timestamp}` under SHA256(channelKeyHex). */
  hmac: string
  /** Desktop only — shown on the phone's server list. */
  desktopName?: string
}

/**
 * Mobile → desktop. `type` is the IPC channel the desktop invokes, and `args`
 * is what the renderer would have passed to `ipcRenderer.invoke`.
 */
export interface MobileMessage {
  type: string
  requestId: string
  args: unknown[]
  /**
   * Stamped by the relay worker on the way through — never set by the sender.
   * The desktop decides whether the frame may invoke an IPC handler from this
   * alone, so a client-supplied value would just say `editor`.
   */
  _role?: RelayRole
}

/**
 * Who can be on a channel.
 *
 *   desktop  the machine
 *   mobile   the owner's own phone — full reach, it is them
 *   editor   a share recipient granted input — same reach as mobile
 *   viewer   a share recipient who may watch and point, nothing else
 *
 * The desktop gates invokeHandler on this (electron/main/relayRoles.ts), so
 * the distinction is a security boundary rather than a label.
 */
export type RelayRole = 'desktop' | 'mobile' | 'editor' | 'viewer'

/** Desktop → mobile. */
export type DesktopMessage =
  /** Worker accepted the auth frame. */
  | { type: 'auth-ok' }
  /** Worker relayed the desktop's name. */
  | { type: 'paired'; desktopName: string }
  /** A peer authenticated / dropped. More than one client can be attached. */
  | { type: 'peer-connected'; role: RelayRole }
  | { type: 'peer-disconnected'; role: RelayRole }
  /** Result of a MobileMessage. */
  | { type: 'response'; requestId: string; data: unknown }
  | { type: 'error'; requestId: string; message: string }
  /** Projects, folders, tabs and terminals, pushed when a phone attaches. */
  | { type: 'state-snapshot'; projects: unknown[]; folders: Record<string, unknown[]>; tabs: Record<string, unknown[]>; terminals: Record<string, unknown[]> }
  /** A mirrored main→renderer event, arguments intact. */
  | { type: 'ipc-event'; channel: string; args: unknown[] }
  /** A JPEG frame from a desktop <webview> the phone is viewing. */
  | { type: 'webview-frame'; tabId: string; data: string; w: number; h: number }
